import type { NarrativeTone } from '@/types/narrative.types';

export const NARRATIVE_TONES: NarrativeTone[] = [
  {
    id: 'barroom-chronicle',
    genreId: 'southern-rock-porch',
    label: 'Barroom Chronicle',
    storytellingDna: 'Long narrative verses, place-name drops, "you had to be there" energy. Stories told like you\'re three beers in and the jukebox just played the right song.',
    lyricConventions: [
      'Name specific places',
      'Let story carry emotion',
      'Concrete details over abstractions',
      'Verses can run long'
    ],
    promptModifier: `[Tone: Barroom Chronicle — Southern Rock]\nLong narrative verses with place-name specificity. "You had to be there" energy. Concrete details over abstractions. Let the story do the emotional work — don't explain what the listener should feel.`
  },
  {
    id: 'outlaw-testimony',
    genreId: 'country-saloon',
    label: 'Outlaw Testimony',
    storytellingDna: 'First-person confession from wrong side. Highway imagery, anti-authority, unapologetic.',
    lyricConventions: [
      'First person always',
      'Highway/motion imagery',
      'Anti-authority undercurrent',
      'Honesty over virtue'
    ],
    promptModifier: `[Tone: Outlaw Testimony — Country Saloon]\nFirst-person confession. Highway imagery, motion as metaphor. The narrator doesn't apologize or moralize. Unapologetic honesty — tell it like it happened, not like it should have.`
  },
  {
    id: 'front-porch-truth',
    genreId: 'muscle-shoals',
    label: 'Front Porch Truth',
    storytellingDna: 'Repetition as emphasis, metaphor over literal, call-and-response echoes. Late and quiet truth.',
    lyricConventions: [
      'Repetition deepens',
      'Metaphor over direct statement',
      'Call-and-response patterns',
      'Sensory language'
    ],
    promptModifier: `[Tone: Front Porch Truth — Muscle Shoals]\nRepetition as emphasis. Metaphor over literal. Call-and-response echoes between sections. Sensory, grounded language — the kind of truth that sits heavy in humid air.`
  },
  {
    id: 'mythic-descent',
    genreId: 'doom-metal',
    label: 'Mythic Descent',
    storytellingDna: 'Biblical/ancient imagery, slow revelation, every word carries weight. Prophecy, inevitable.',
    lyricConventions: [
      'Biblical/mythic imagery',
      'Slow pacing, fewer words',
      'Inevitability over surprise',
      'Abstract and concrete interleaved'
    ],
    promptModifier: `[Tone: Mythic Descent — Doom Metal]\nBiblical/ancient imagery. Slow revelation. Every word earns its place. The narrative feels inevitable — not a story being told, but a prophecy being fulfilled.`
  },
  {
    id: 'witness-testimony',
    genreId: 'stax',
    label: 'Witness Testimony',
    storytellingDna: 'Building testimony, darkness to light, personal to communal triumph.',
    lyricConventions: [
      'Personal to communal shift',
      'Darkness-to-light',
      'Testimony phrasing',
      'Emotional crescendo'
    ],
    promptModifier: `[Tone: Witness Testimony — Stax]\nTestimony structure: personal struggle building to communal declaration. "I" becomes "we." Each section builds emotional intensity. The narrator has been through it and is telling you what they found on the other side.`
  },
  {
    id: 'postcard-story',
    genreId: 'americana',
    label: 'Postcard Story',
    storytellingDna: 'Snapshot scenes, hyper-specific details, emotion shown not stated.',
    lyricConventions: [
      'Specific details (brand names, route numbers)',
      'Show don\'t tell',
      'Snapshot scenes',
      'Understated'
    ],
    promptModifier: `[Tone: Postcard Story — Americana]\nSnapshot scenes with hyper-specific details. Brand names, road numbers, exact times. Emotion shown through what's described, never stated directly. The power lives in what's left unsaid.`
  },
  {
    id: 'war-hymn',
    genreId: 'southern-metal',
    label: 'War Hymn',
    storytellingDna: 'Declarative, epic scale, us-vs-them, anthemic repetition. Written to be shouted.',
    lyricConventions: [
      'Declarative not introspective',
      'Epic scale imagery',
      'Us-vs-them framing',
      'Anthemic chorus'
    ],
    promptModifier: `[Tone: War Hymn — Southern Metal]\nDeclarative statements at epic scale. Us-vs-them framing. The chorus is a rallying cry meant to be shouted by a crowd. No ambiguity — this is a declaration, not a conversation.`
  },
  {
    id: 'street-sermon',
    genreId: 'flyte-time',
    label: 'Street Sermon',
    storytellingDna: 'Rhythmic wordplay, swagger, conversational asides, humor with truth.',
    lyricConventions: [
      'Rhythmic wordplay that grooves',
      'Conversational asides',
      'Humor delivers truth',
      'Swagger'
    ],
    promptModifier: `[Tone: Street Sermon — Flyte Time]\nRhythmic wordplay that grooves. Conversational asides. Humor woven through serious points. The narrator has swagger — they know they're performing, and that's part of the message.`
  },
  {
    id: 'southern-epic',
    genreId: 'capricorn',
    label: 'Southern Epic',
    storytellingDna: 'Sprawling ambition, South as mythic landscape, Faulkner meets Skynyrd.',
    lyricConventions: [
      'South as character',
      'Multi-generational hints',
      'Epic scope compressed',
      'Mythologize the mundane'
    ],
    promptModifier: `[Tone: Southern Epic — Capricorn]\nSprawling narrative ambition. The South as mythic landscape. Mundane events mythologized into legend. Multiple voices or timelines hinted at within a single song's scope.`
  },
  {
    id: 'velvet-confession',
    genreId: 'philly',
    label: 'Velvet Confession',
    storytellingDna: 'Lush romantic vulnerability, sophisticated language, heartbreak in silk.',
    lyricConventions: [
      'Sophisticated vocabulary',
      'Romantic/sensual imagery',
      'Vulnerability as strength',
      'Flowing phrasing'
    ],
    promptModifier: `[Tone: Velvet Confession — Philly Soul]\nLush romantic vulnerability in sophisticated language. Heartbreak in silk. The narrator feels deeply and articulates it beautifully. Words should flow like the melody they'll be sung over.`
  },
  {
    id: 'assembly-line-blues',
    genreId: 'motown',
    label: 'Assembly Line Blues',
    storytellingDna: 'Universal emotions, working-class specificity, chorus is king, efficient storytelling.',
    lyricConventions: [
      'Universal emotion, specific detail',
      'Chorus is king',
      'Working-class imagery',
      'Efficient storytelling'
    ],
    promptModifier: `[Tone: Assembly Line Blues — Motown]\nUniversal emotion delivered through working-class specificity. The chorus is the hook — must be instantly memorable and singable. No wasted lines. Every word earns its place.`
  },
  {
    id: 'dustbowl-dispatch',
    genreId: 'bakersfield',
    label: 'Dustbowl Dispatch',
    storytellingDna: 'Lean hard-bitten storytelling, California-country edge, no sentimentality, grits teeth.',
    lyricConventions: [
      'Lean language',
      'Working-class realism',
      'Grit over polish',
      'Imagery from labor/land/machinery'
    ],
    promptModifier: `[Tone: Dustbowl Dispatch — Bakersfield]\nLean, hard-bitten storytelling. Working people, real problems, no sentimentality. Trim every unnecessary word. The Bakersfield sound doesn't cry — it grits its teeth and keeps going.`
  },
  {
    id: 'music-row-polish',
    genreId: 'nashville',
    label: 'Music Row Polish',
    storytellingDna: 'Craft-forward songwriting, emotional precision, clever not cute.',
    lyricConventions: [
      'Every line serves the hook',
      'Clever wordplay and double meanings',
      'Emotional precision',
      'Bridge as reveal'
    ],
    promptModifier: `[Tone: Music Row Polish — Nashville]\nCraft-forward songwriting. Every line serves the hook. Clever wordplay without being cute. Emotional precision — say exactly what you mean. Save the best twist for the bridge.`
  }
];

export function getNarrativeToneByGenre(genreId: string): NarrativeTone | undefined {
  return NARRATIVE_TONES.find(t => t.genreId === genreId);
}

export function getNarrativeToneById(id: string): NarrativeTone | undefined {
  return NARRATIVE_TONES.find(t => t.id === id);
}
